import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BarChart3, Eye, Calendar, CheckCircle, DollarSign, TrendingUp,
  ArrowLeft, ChevronRight, AlertCircle, Camera, Tag,
  Star, Clock, RefreshCw, Car, Lightbulb, Target, Award,
  AlertTriangle, Info, ThumbsUp, Edit
} from 'lucide-react';
import { toast } from 'sonner';
import { getVehiclePhotoUrl, handleVehicleImageError } from '../lib/imageUtils';

// Simple chart component
const TrendChart = ({ data, type = 'bar', color = '#004B8D', height = 140 }) => {
  if (!data || data.length === 0) {
    return (
      <div className={`flex items-center justify-center bg-slate-50 rounded-lg`} style={{ height }}>
        <p className="text-sm text-slate-400">No data yet</p>
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d.count || d.amount || 0));

  return (
    <div className="relative" style={{ height }}>
      <div className="flex items-end justify-between h-full gap-0.5">
        {data.slice(-30).map((item, index) => {
          const value = item.count || item.amount || 0;
          const heightPercent = maxValue > 0 ? (value / maxValue) * 100 : 0;
          return (
            <div
              key={index}
              className="flex-1 min-w-[4px] rounded-t transition-all hover:opacity-80"
              style={{
                height: `${Math.max(heightPercent, 2)}%`,
                backgroundColor: color,
                opacity: 0.7 + (index / data.length) * 0.3
              }}
              title={`${item.date}: ${value}`}
            />
          );
        })}
      </div>
    </div>
  );
};

const VehicleAnalytics = () => {
  const { vehicleId } = useParams();
  const { api, isHost } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    if (!isHost || !isHost()) {
      navigate('/host');
      return;
    }
    fetchAnalytics();
  }, [vehicleId, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/host/analytics/vehicle/${vehicleId}?days=${period}`);
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching vehicle analytics:', error);
      toast.error('Failed to load vehicle analytics');
      navigate('/host/analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'success': return ThumbsUp;
      case 'warning': return AlertTriangle;
      case 'info': return Info;
      default: return Lightbulb;
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'success': return 'bg-emerald-50 border-emerald-200 text-emerald-700';
      case 'warning': return 'bg-amber-50 border-amber-200 text-amber-700';
      case 'info': return 'bg-blue-50 border-blue-200 text-blue-700';
      default: return 'bg-slate-50 border-slate-200 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[#004B8D] mx-auto mb-3" />
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const vehicle = analytics?.vehicle || {};
  const summary = analytics?.summary || {};
  const conversions = analytics?.conversions || {};
  const trends = analytics?.trends || {};
  const insights = analytics?.insights || [];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/host/analytics')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                {vehicle.photo && (
                  <div className="w-12 h-9 rounded-lg overflow-hidden bg-slate-100 hidden sm:block">
                    <img
                      src={getVehiclePhotoUrl(vehicle.photo)}
                      alt={vehicle.name}
                      className="w-full h-full object-cover"
                      onError={handleVehicleImageError}
                    />
                  </div>
                )}
                <div>
                  <h1 className="font-heading text-lg sm:text-xl font-semibold text-slate-900">
                    {vehicle.name}
                  </h1>
                  <p className="text-sm text-slate-500 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Vehicle Analytics
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {[7, 30, 90, 0].map((days) => (
                <Button
                  key={days}
                  variant={period === days ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPeriod(days)}
                  className={`text-xs sm:text-sm ${period === days ? 'bg-[#004B8D]' : ''}`}
                >
                  {days === 0 ? 'All' : `${days}d`}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="vehicle-summary-cards">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-slate-500 font-medium">Views</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="vehicle-views">
                {summary.views?.toLocaleString() || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-amber-500" />
                <span className="text-xs text-slate-500 font-medium">Requests</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="vehicle-requests">
                {summary.booking_requests || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-xs text-slate-500 font-medium">Completed</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="vehicle-completed">
                {summary.completed_trips || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 border-0">
            <CardContent className="p-4 text-white">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs font-medium opacity-90">Net Earnings</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold" data-testid="vehicle-earnings">
                {formatCurrency(summary.net_earnings || 0)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Conversion + Rating Row */}
        <div className="grid sm:grid-cols-2 gap-4">
          {/* Conversion Rates */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Target className="w-4 h-4 text-[#004B8D]" />
                Conversion Rates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">View → Request</span>
                  <span className="font-semibold text-slate-900" data-testid="vehicle-view-request-rate">
                    {conversions.view_to_request || 0}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(conversions.view_to_request || 0, 100)}%` }}
                  />
                </div>

                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-slate-600">Request → Approved</span>
                  <span className="font-semibold text-slate-900">
                    {conversions.request_to_approved || 0}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className="bg-amber-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(conversions.request_to_approved || 0, 100)}%` }}
                  />
                </div>

                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-slate-600">Approved → Completed</span>
                  <span className="font-semibold text-slate-900">
                    {conversions.approved_to_completed || 0}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className="bg-emerald-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(conversions.approved_to_completed || 0, 100)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rating & Stats */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-500" />
                Rating & Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div>
                    <p className="text-sm text-slate-500">Average Rating</p>
                    <div className="flex items-center gap-2 mt-1">
                      {summary.avg_rating ? (
                        <>
                          <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                          <span className="text-2xl font-bold text-slate-900" data-testid="vehicle-rating">
                            {summary.avg_rating}
                          </span>
                          <span className="text-sm text-slate-500">
                            ({summary.review_count} reviews)
                          </span>
                        </>
                      ) : (
                        <span className="text-slate-400">No reviews yet</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500 mb-1">Photos</p>
                    <p className="text-lg font-semibold text-slate-900 flex items-center gap-1">
                      <Camera className="w-4 h-4 text-slate-400" />
                      {vehicle.photo_count || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500 mb-1">Daily Rate</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {vehicle.daily_rate ? formatCurrency(vehicle.daily_rate) : 'Price not set'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <span className="text-sm text-slate-500">Gross Earnings</span>
                  <span className="font-medium text-slate-700">
                    {formatCurrency(summary.gross_earnings || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Platform Fees</span>
                  <span className="font-medium text-red-500">
                    -{formatCurrency(summary.platform_fees || 0)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trend Charts */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-500" />
                Views Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TrendChart data={trends.views} color="#3b82f6" />
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-500" />
                Bookings Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TrendChart data={trends.bookings} color="#f59e0b" />
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-500" />
                Earnings Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TrendChart data={trends.earnings} color="#10b981" />
            </CardContent>
          </Card>
        </div>

        {/* Insights */}
        {insights.length > 0 && (
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Insights & Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="vehicle-insights">
                {insights.map((insight, index) => {
                  const Icon = getInsightIcon(insight.type);
                  return (
                    <div
                      key={index}
                      className={`p-4 rounded-xl border ${getInsightColor(insight.type)}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-white/60 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="font-medium text-sm mb-1">{insight.title}</h4>
                          <p className="text-xs opacity-80">{insight.message}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate(`/host/vehicles/${vehicleId}/edit`)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Listing
          </Button>
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate(`/vehicles/${vehicleId}`)}
          >
            <Eye className="w-4 h-4 mr-2" />
            View Public Listing
          </Button>
        </div>
      </main>
    </div>
  );
};

export default VehicleAnalytics;
